/**
 * 스트리밍 채팅 서비스
 * EventSource와 fetch 기반 스트리밍을 처리하는 독립적인 모듈
 */

import { processVisualizationData as processVisualizationDataHelper } from '../utils/visualizationHelper';
import { formatMarkdownLike } from '../utils/markdownFormatter';

/**
 * 스트리밍 서비스를 위한 콜백 인터페이스
 */
export const StreamingCallbacks = {
  onStreamStart: () => {},
  onTextUpdate: (text) => {},
  onVisualizationUpdate: (visualizations) => {},
  onBlocksUpdate: (blocks) => {},
  onError: (error) => {},
  onComplete: (finalData) => {},
  onLoadingChange: (isLoading) => {},
  onChatHistoryUpdate: (historyUpdater) => {},
  onMessageIdReceived: (messageId) => {}
};

/**
 * EventSource 기반 스트리밍 처리 클래스
 */
export class EventSourceStreamer {
  constructor(callbacks = {}) {
    this.callbacks = { ...StreamingCallbacks, ...callbacks };
    this.eventSource = null;
    this.streamingText = '';
    this.currentVisualizations = [];
    this.streamingDisplayBlocks = [];
    this.streamingDisplayBlocksRef = { current: [] };
    this.visualizationsRef = { current: [] };
  }

  /**
   * EventSource 스트리밍 시작
   */
  start(url, requestData, isDeveloperMode = false) {
    // 이전 연결이 있으면 정리
    this.cleanup();
    
    // 시각화 데이터 초기화
    this.currentVisualizations = [];
    this.callbacks.onVisualizationUpdate([]);
    
    // 디버깅 로그 추가
    console.log("EventSource 설정 시작", url, requestData);
    
    // POST 요청을 위한 URL 인코딩
    const params = new URLSearchParams();
    for (const key in requestData) {
      params.append(key, requestData[key]);
    }
    
    const fullUrl = `${url}?${params.toString()}`;
    console.log("SSE 연결 URL:", fullUrl);
    
    // EventSource 연결 설정
    const eventSource = new EventSource(fullUrl);
    this.eventSource = eventSource;
    
    // 스트리밍 시작 시 상태 초기화
    this.streamingText = '';
    this.callbacks.onTextUpdate('');
    
    // 개발자 모드를 위한 메타데이터 컬렉션
    let metadataCollection = [];
    
    // 연결 열림 이벤트 처리
    eventSource.onopen = (event) => {
      console.log("SSE 연결이 열렸습니다", event);
    };
    
    // 메시지 이벤트 처리
    eventSource.onmessage = (event) => {
      // 확인: 이벤트 데이터 문자열이 있는지
      if (!event.data || event.data.trim() === "") {
        console.warn("빈 이벤트 데이터 수신됨");
        return;
      }
      
      try {
        // 수신된 JSON 데이터 파싱
        const data = JSON.parse(event.data);
        
        // 로딩 상태 관리
        if (data.start) {
          console.log("스트리밍 응답 시작됨", data.message_id);
          this.callbacks.onLoadingChange(true);
        }
        
        // 텍스트 청크 처리
        if (data.text) {
          this.streamingText += data.text;
          this.callbacks.onTextUpdate(this.streamingText);
        }
        
        // 통합 시각화 처리 함수 호출
        processVisualizationDataHelper(
          data,
          (visualizations) => {
            this.currentVisualizations = visualizations;
            this.callbacks.onVisualizationUpdate(visualizations);
          },
          (blocks) => {
            this.streamingDisplayBlocks = blocks;
            this.streamingDisplayBlocksRef.current = blocks;
            this.callbacks.onBlocksUpdate(blocks);
          },
          this.streamingDisplayBlocksRef,
          this.visualizationsRef,
          isDeveloperMode
        );
        
        // 개발자 모드 메타데이터 처리
        if (data.type === 'metadata') {
          if (data.tool && isDeveloperMode) {
            const newline = "\n\n";
            const toolMarkdown = data.tool.markdown;
            this.streamingText += newline + toolMarkdown + newline;
            this.callbacks.onTextUpdate(this.streamingText);
          } 
          else if (data.tool_result && isDeveloperMode) {
            const newline = "\n\n";
            const resultMarkdown = data.tool_result.markdown;
            this.streamingText += newline + resultMarkdown + newline;
            this.callbacks.onTextUpdate(this.streamingText);
          }
        }
        
        // 오류 처리
        if (data.error) {
          console.error("스트리밍 오류:", data.error);
          this.callbacks.onChatHistoryUpdate(prev => [
            ...prev, 
            { 
              role: 'error',
              content: `오류: ${data.error}`
            }
          ]);
          this.callbacks.onLoadingChange(false);
          eventSource.close();
          this.eventSource = null;
          return;
        }
        
        // 종료 처리
        if (data.done) {
          console.log("스트리밍 응답 완료됨", data.stop_reason);
          console.log("수집된 시각화 데이터:", this.currentVisualizations.length);
          this.callbacks.onLoadingChange(false);
          eventSource.close();
          
          // 시각화 데이터가 있는지 확인하고 로그 출력
          if (this.currentVisualizations.length > 0) {
            console.log("채팅 기록에 추가되는 시각화 개수:", this.currentVisualizations.length);
            console.log("시각화 데이터:", this.currentVisualizations);
          }
          
          // 현재 시각화 데이터의 깊은 복사본 생성
          const visualizationsCopy = this.currentVisualizations.length > 0 
            ? JSON.parse(JSON.stringify(this.currentVisualizations)) 
            : [];
          
          // 응답 메시지에 HTML로 포맷팅된 내용도 함께 저장
          const formattedHtml = formatMarkdownLike(this.streamingText, isDeveloperMode);
          
          // 최종 데이터 콜백 호출
          const finalData = { 
            role: 'assistant', 
            content: this.streamingText,
            formatted: formattedHtml,
            visualizations: visualizationsCopy
          };
          
          this.callbacks.onComplete(finalData);
        }
      } catch (error) {
        console.error("SSE 메시지 처리 오류:", error, "원본 데이터:", event.data);
      }
    };
    
    // 오류 이벤트 처리
    eventSource.onerror = (error) => {
      console.error("SSE 연결 오류:", error);
      this.callbacks.onError("서버 연결 오류가 발생했습니다. 새로고침 후 다시 시도해주세요.");
      this.callbacks.onLoadingChange(false);
      eventSource.close();
    };
    
    return eventSource;
  }

  /**
   * 연결 정리
   */
  cleanup() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}

/**
 * Fetch 기반 스트리밍 처리 클래스
 */
export class FetchStreamer {
  constructor(callbacks = {}) {
    this.callbacks = { ...StreamingCallbacks, ...callbacks };
    this.abortController = null;
    this.streamingDisplayBlocksRef = { current: [] };
    this.visualizationsRef = { current: [] };
  }

  /**
   * Fetch 기반 스트리밍 시작
   */
  async start(requestUrl, isDeveloperMode = false) {
    // 기존 요청 취소
    if (this.abortController) {
      this.abortController.abort();
    }
    
    const controller = new AbortController();
    this.abortController = controller;
    
    console.log("스트리밍 요청 URL:", requestUrl);
    
    try {
      // fetch 및 ReadableStream 사용
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'Connection': 'keep-alive',
        },
        signal: controller.signal,
        mode: 'cors',
        cache: 'no-store',
        credentials: 'include'
      });

      console.log("응답 상태:", response.status);
      console.log("응답 헤더:", Object.fromEntries([...response.headers.entries()]));
      
      if (!response.ok) {
        throw new Error(`HTTP 오류: ${response.status}, ${response.statusText}`);
      }
      
      if (!response.body) {
        throw new Error('ReadableStream이 지원되지 않습니다');
      }
      
      // 스트림 처리를 위한 설정
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';
      
      // 청크 처리 함수
      const processChunks = async () => {
        try {
          const { value, done } = await reader.read();
          
          if (done) {
            // 스트림 종료 시 최종 블록 상태를 ref에서 가져오기
            const finalBlocks = this.streamingDisplayBlocksRef.current;
            
            if (isDeveloperMode) {
              console.log("스트림 종료, 최종 블록 개수:", finalBlocks.length);
            }
            
            // 최종 블록을 단일 메시지로 저장
            if (finalBlocks && finalBlocks.length > 0) {
              const finalBlocksCopy = JSON.parse(JSON.stringify(finalBlocks));
              
              if (isDeveloperMode) {
                console.log('채팅 완료 - 기록 저장 (스트림 종료 시, 블록 구조)');
                console.log('저장할 블록 데이터 복사본:', finalBlocksCopy.length, '개');
              }
              
              // 최종 데이터 콜백 호출
              const finalData = {
                role: 'assistant',
                blocks: finalBlocksCopy
              };
              
              this.callbacks.onComplete(finalData);
            } else {
              console.warn("스트림 종료되었지만 최종 블록이 없습니다.");
              this.callbacks.onComplete({
                role: 'assistant',
                blocks: []
              });
            }
            
            this.callbacks.onLoadingChange(false);
            return;
          }
          
          // 텍스트 디코딩
          const chunk = decoder.decode(value, { stream: true });
          // console.log("Raw Decoded Chunk:", JSON.stringify(chunk)); 

          buffer += chunk; // 청크를 버퍼에 추가

          while (true) {
            const dataStartIndex = buffer.indexOf('data: ');
            if (dataStartIndex === -1) {
              break; 
            }

            if (dataStartIndex > 0) {
              buffer = buffer.substring(dataStartIndex);
            }

            const messageEndIndex = buffer.indexOf('\n\n');
            if (messageEndIndex === -1) {
              break;
            }

            // 수정: 올바른 이벤트 문자열 추출
            const currentMessageBlock = buffer.substring(0, messageEndIndex);
            buffer = buffer.substring(messageEndIndex + 2); // \n\n 제거

            // 'data: ' 접두사 제거하여 실제 JSON 문자열 추출
            const eventString = currentMessageBlock.substring(6).trim();

            if (eventString) {
              try {
                // console.log("Parsing eventString from refined logic:", JSON.stringify(eventString));
                const eventData = JSON.parse(eventString);

                if (eventData.type === 'stream_error') {
                  console.error("치명적 스트리밍 오류 수신:", eventData.error_message);
                  this.callbacks.onChatHistoryUpdate(prev => [
                    ...prev, 
                    { role: 'error', content: `오류: ${eventData.error_message || '알 수 없는 치명적 오류'}` }
                  ]);
                  this.callbacks.onLoadingChange(false);
                  return; 
                }
                else if (eventData.type === 'tool_execution_error') {
                  console.warn("도구 실행 오류 수신:", eventData.error_message);
                  this.callbacks.onBlocksUpdate(prevBlocks => {
                    const newBlocks = [...prevBlocks, { type: 'warning', content: `도구 실행 중 문제 발생: ${eventData.error_message}`, tool_id: eventData.tool_id }];
                    this.streamingDisplayBlocksRef.current = newBlocks;
                    return newBlocks;
                  });
                }
                else if (eventData.error) {
                  console.warn("하위 호환성 오류 감지:", eventData.error);
                  this.callbacks.onBlocksUpdate(prevBlocks => {
                    const newBlocks = [...prevBlocks, { type: 'warning', content: `처리 중 문제 발생: ${eventData.error}` }];
                    this.streamingDisplayBlocksRef.current = newBlocks;
                    return newBlocks;
                  });
                }
                
                // 중단 신호 처리 추가
                if (eventData.cancelled) {
                  console.log("중단 신호 수신:", eventData.reason);
                  
                  // 중단 메시지를 블록에 추가
                  this.callbacks.onBlocksUpdate(prevBlocks => {
                    const cancelMessage = eventData.reason === 'cancelled_before_start' 
                      ? '응답 시작 전에 중단되었습니다.'
                      : eventData.reason === 'cancelled_during_streaming'
                      ? '응답 중에 중단되었습니다.'
                      : eventData.reason === 'cancelled_during_tool_loop'
                      ? '데이터 분석 중에 중단되었습니다.'
                      : eventData.reason === 'cancelled_before_tool_execution'
                      ? '도구 실행 전에 중단되었습니다.'
                      : eventData.reason === 'cancelled_before_db_query'
                      ? 'DB 쿼리 실행 전에 중단되었습니다.'
                      : eventData.reason === 'cancelled_after_db_query'
                      ? 'DB 쿼리 실행 후에 중단되었습니다.'
                      : '응답이 중단되었습니다.';
                      
                    const newBlocks = [...prevBlocks, { 
                      type: 'text', 
                      content: `\n\n⏹ **${cancelMessage}**\n\n`,
                      cancelled: true 
                    }];
                    this.streamingDisplayBlocksRef.current = newBlocks;
                    return newBlocks;
                  });
                  
                  // 중단 콜백 호출 (있는 경우)
                  if (this.callbacks.onCancelled) {
                    this.callbacks.onCancelled();
                  }
                  
                  // 로딩 상태 종료
                  this.callbacks.onLoadingChange(false);
                  
                  // 최종 데이터로 현재 블록 저장
                  const finalBlocks = this.streamingDisplayBlocksRef.current;
                  if (finalBlocks && finalBlocks.length > 0) {
                    const finalBlocksCopy = JSON.parse(JSON.stringify(finalBlocks));
                    this.callbacks.onComplete({
                      role: 'assistant',
                      blocks: finalBlocksCopy,
                      cancelled: true
                    });
                  }
                  
                  return; // 스트림 처리 종료
                }

                if (processVisualizationDataHelper(
                  eventData,
                  (visualizations) => this.callbacks.onVisualizationUpdate(visualizations),
                  (blocks) => {
                    this.streamingDisplayBlocksRef.current = blocks;
                    this.callbacks.onBlocksUpdate(blocks);
                  },
                  this.streamingDisplayBlocksRef,
                  this.visualizationsRef,
                  isDeveloperMode
                )) {
                  // 시각화 데이터 처리됨
                } else if (eventData.text) {
                  fullText += eventData.text; 

                  this.callbacks.onBlocksUpdate(prevBlocks => {
                    let newBlocks = prevBlocks;
                    if (prevBlocks.length > 0 && prevBlocks[prevBlocks.length - 1].type === 'text') {
                      const lastBlock = prevBlocks[prevBlocks.length - 1];
                      newBlocks = [...prevBlocks.slice(0, -1), { ...lastBlock, content: lastBlock.content + eventData.text }];
                    } else {
                      newBlocks = [...prevBlocks, { type: 'text', content: eventData.text }];
                    }
                    this.streamingDisplayBlocksRef.current = newBlocks;
                    return newBlocks;
                  });
                }
                
                if (eventData.init) {
                  console.log("초기화 이벤트:", eventData); 
                  if (eventData.session_id) { /* ... */ }
                  // 실제 Claude 메시지 ID를 콜백으로 전달
                  if (eventData.message_id && this.callbacks.onMessageIdReceived) {
                    this.callbacks.onMessageIdReceived(eventData.message_id);
                  }
                }
              } catch (e) {
                console.error("정교화된 로직 이벤트 파싱 오류:", e, "Problematic eventString:", JSON.stringify(eventString));
              }
            }
          }
          
          // 다음 청크 처리
          await processChunks();
          
        } catch (error) {
          if (error.name === 'AbortError') {
            console.log("스트림 읽기 중단됨");
          } else {
            console.error("스트림 읽기 오류:", error);
            this.callbacks.onError(`스트림 읽기 오류: ${error.message}`);
            this.callbacks.onLoadingChange(false);
          }
        }
      };
      
      // 첫 청크 처리 시작
      await processChunks();
      
    } catch (error) {
      console.error("스트리밍 요청 오류:", error);
      this.callbacks.onError(`스트리밍 요청 오류: ${error.message}`);
      this.callbacks.onLoadingChange(false);
    }
  }

  /**
   * 연결 정리
   */
  cleanup() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
}

/**
 * 일반 모드 API 호출 함수 (비스트리밍)
 */
export const fetchChatResponse = async (url, requestData) => {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestData),
  });
  
  const data = await res.json();
  
  if (!res.ok) {
    throw new Error(data.error || '오류가 발생했습니다.');
  }
  
  return data;
};

/**
 * 스트리밍 서비스 팩토리 함수
 */
export const createStreamingService = (type = 'fetch', callbacks = {}) => {
  switch (type) {
    case 'eventsource':
      return new EventSourceStreamer(callbacks);
    case 'fetch':
    default:
      return new FetchStreamer(callbacks);
  }
};

/**
 * 기본 내보내기: 가장 일반적인 fetch 스트리머
 */
export default {
  EventSourceStreamer,
  FetchStreamer,
  fetchChatResponse,
  createStreamingService
}; 